import { useState, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import SeoHelmet from '@/components/feature/SeoHelmet';
import ComicCard from '@/components/base/ComicCard';
import { comics, comicCategories, koreanComics, japaneseComics, chineseComics } from '@/mocks/comics';

const categoryMap: Record<string, { title: string; description: string; keywords: string; data: typeof comics }> = {
  korean: {
    title: '丫丫漫画韩漫 | 丫丫漫画韩漫热门分类大全',
    description: '丫丫漫画韩漫频道，收录海量热门韩国漫画，高颜值画风，剧情引人入胜，免费全集下拉式阅读，好看的韩漫一网打尽。',
    keywords: '丫丫漫画韩漫,韩国漫画,韩漫免费,韩漫全集,韩漫下拉式,好看的韩漫,热门韩漫',
    data: koreanComics,
  },
  japanese: {
    title: '丫丫漫画日漫 | 丫丫漫画日漫热门分类大全',
    description: '丫丫漫画日漫频道，经典日漫全集免费阅读，热血、恋爱、奇幻应有尽有，下拉式流畅阅读体验，每日同步更新。',
    keywords: '丫丫漫画日漫,日本漫画,日漫免费,日漫全集,日漫下拉式,好看的日漫,热门日漫',
    data: japaneseComics,
  },
  chinese: {
    title: '丫丫漫画国漫 | 丫丫漫画国漫热门分类大全',
    description: '丫丫漫画国漫频道，国产漫画崛起之作，古风与现代完美融合，玄幻、仙侠、都市应有尽有，免费下拉式阅读。',
    keywords: '丫丫漫画国漫,国产漫画,国漫免费,国漫全集,国漫下拉式,好看的国漫,热门国漫',
    data: chineseComics,
  },
};

export default function CategoryPage() {
  const { type = 'korean' } = useParams<{ type: string }>();
  const [activeTab, setActiveTab] = useState(type);
  const [sortBy, setSortBy] = useState<'popular' | 'newest'>('popular');

  const currentCategory = categoryMap[activeTab] || categoryMap.korean;
  const categoryInfo = comicCategories.find((c) => c.id === activeTab);

  const sortedComics = useMemo(() => {
    const data = [...currentCategory.data];
    if (sortBy === 'newest') {
      return data.sort((a, b) => b.chapters - a.chapters);
    }
    return data;
  }, [currentCategory.data, sortBy]);

  const handleTabChange = (newType: string) => {
    if (newType === activeTab) return;
    setActiveTab(newType);
    window.history.pushState(null, '', `${__BASE_PATH__}/category/${newType}`);
  };

  const breadcrumbItems = [
    { label: '首页', path: '/' },
    { label: categoryInfo?.name || '分类' },
  ];

  const pageSchema = [
    {
      '@context': 'https://schema.org',
      '@type': 'CollectionPage',
      name: currentCategory.title,
      description: currentCategory.description,
      isPartOf: {
        '@type': 'WebSite',
        name: '丫丫漫画',
      },
      inLanguage: 'zh-CN',
      mainEntity: {
        '@type': 'ItemList',
        itemListElement: sortedComics.map((comic, index) => ({
          '@type': 'ListItem',
          position: index + 1,
          item: {
            '@type': 'CreativeWork',
            name: comic.title,
            author: { '@type': 'Person', name: comic.author },
          },
        })),
      },
    },
  ];

  return (
    <>
      <ScrollToTop />
      <SeoHelmet
        title={currentCategory.title}
        description={currentCategory.description}
        keywords={currentCategory.keywords}
        canonicalPath={`/category/${activeTab}`}
        ogType="website"
        schema={pageSchema}
      />
      <Navbar />
      <main className="pt-16">
        <Breadcrumb items={breadcrumbItems} />

        <section className="w-full px-4 md:px-6 py-8 bg-background-50">
          <div className="max-w-6xl mx-auto">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
              <div>
                <h1 className="text-2xl md:text-3xl font-heading text-foreground-950">
                  {categoryInfo?.name}热门漫画
                </h1>
                <p className="mt-1 text-sm text-foreground-500">
                  {categoryInfo?.description}
                </p>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
              <div className="flex items-center gap-1 bg-background-100 rounded-full p-1">
                {comicCategories.map((cat) => (
                  <button
                    key={cat.id}
                    onClick={() => handleTabChange(cat.id)}
                    className={`px-5 py-2 rounded-full text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                      activeTab === cat.id
                        ? 'bg-primary-500 text-background-50'
                        : 'text-foreground-600 hover:text-foreground-900'
                    }`}
                  >
                    {cat.name}
                  </button>
                ))}
              </div>

              <div className="flex items-center gap-2">
                <button
                  onClick={() => setSortBy('popular')}
                  className={`px-4 py-2 rounded-lg text-sm transition-colors whitespace-nowrap cursor-pointer ${
                    sortBy === 'popular'
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-background-100 text-foreground-600 hover:text-foreground-900'
                  }`}
                >
                  按热度
                </button>
                <button
                  onClick={() => setSortBy('newest')}
                  className={`px-4 py-2 rounded-lg text-sm transition-colors whitespace-nowrap cursor-pointer ${
                    sortBy === 'newest'
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-background-100 text-foreground-600 hover:text-foreground-900'
                  }`}
                >
                  按更新
                </button>
              </div>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 md:gap-5" data-product-shop="">
              {sortedComics.map((comic) => (
                <ComicCard
                  key={comic.id}
                  id={comic.id}
                  title={comic.title}
                  author={comic.author}
                  cover={comic.cover}
                  tags={comic.tags}
                  status={comic.status}
                  updateInfo={comic.updateInfo}
                  views={comic.views}
                  rating={comic.rating}
                  isNew={comic.isNew}
                />
              ))}
            </div>

            {sortedComics.length === 0 && (
              <div className="text-center py-20">
                <p className="text-foreground-500">该分类暂无漫画</p>
              </div>
            )}
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}